import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { fetchConfirmationDetails, submitConfirmation, type ConfirmDecision } from "../services/confirm.service";
import { LoadingSpinner } from "../components/LoadingSpinner";
import type { ConfirmPageData } from "../types";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
}

type Choice = "ATTENDING" | "NOT_ATTENDING" | "UNDECIDED";

function choiceFromStatus(status: ConfirmPageData["children"][number]["status"]): Choice {
  if (status === "CONFIRMED") return "ATTENDING";
  if (status === "DECLINED") return "NOT_ATTENDING";
  return "UNDECIDED";
}

export default function ConfirmPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const queryClient = useQueryClient();
  const [choices, setChoices] = useState<Record<string, Choice>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [initialized, setInitialized] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["confirm", token],
    queryFn: () => fetchConfirmationDetails(token!),
    enabled: !!token,
  });

  // Prefill each child's choice/reason from whatever is already recorded
  // server-side, so a refresh (or a parent returning later to change their
  // mind) shows the current state instead of a blank/locked form. Only runs
  // once per successful load — after that, the form is user-controlled so
  // edits aren't stomped by background refetches.
  useEffect(() => {
    if (!data?.ok || !data.data || initialized) return;
    const nextChoices: Record<string, Choice> = {};
    const nextReasons: Record<string, string> = {};
    for (const child of data.data.children) {
      nextChoices[child.studentId] = choiceFromStatus(child.status);
      if (child.declineReason) nextReasons[child.studentId] = child.declineReason;
    }
    setChoices(nextChoices);
    setReasons(nextReasons);
    setInitialized(true);
  }, [data, initialized]);

  const mutation = useMutation({
    mutationFn: () => {
      const decisions: ConfirmDecision[] = Object.entries(choices)
        .filter(([, choice]) => choice !== "UNDECIDED")
        .map(([studentId, choice]) => ({
          studentId,
          decision: choice as "ATTENDING" | "NOT_ATTENDING",
          reason: choice === "NOT_ATTENDING" ? reasons[studentId]?.trim() || undefined : undefined,
        }));
      return submitConfirmation(token!, decisions);
    },
    onSuccess: (children) => {
      queryClient.setQueryData(["confirm", token], { ok: true, data: { ...data!.data!, children } });
      setJustSubmitted(true);
    },
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-app-light bg-fixed bg-slate-50 dark:bg-none dark:bg-slate-900 px-4">
        <LoadingSpinner label="Loading confirmation..." />
      </div>
    );
  }

  if (!data?.ok || !data.data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-app-light bg-fixed bg-slate-50 dark:bg-none dark:bg-slate-900 px-4">
        <div className="card w-full max-w-sm p-6 text-center">
          <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Link unavailable</h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            {data?.errorMessage ?? "This confirmation link has expired. Please contact the event organiser for a new link."}
          </p>
        </div>
      </div>
    );
  }

  const details = data.data;
  const hasAnyResponse = details.children.some((c) => c.status !== "PENDING");

  return (
    <div className="min-h-screen bg-app-light bg-fixed bg-slate-50 dark:bg-none dark:bg-slate-900 px-4 py-8">
      <div className="mx-auto w-full max-w-md animate-fade-in-up">
        <div className="card p-6 shadow-card-hover">
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">{details.event.name}</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{details.event.courseName}</p>

          <dl className="mt-4 space-y-1 text-sm text-slate-700 dark:text-slate-300">
            <div className="flex justify-between">
              <dt className="text-slate-500 dark:text-slate-400">Date</dt>
              <dd>{formatDate(details.event.eventDate)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500 dark:text-slate-400">Time</dt>
              <dd>
                {details.event.startTime} - {details.event.endTime}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500 dark:text-slate-400">Location</dt>
              <dd className="text-right">{details.event.location}</dd>
            </div>
          </dl>

          {details.event.description && <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{details.event.description}</p>}

          <hr className="my-4 border-slate-200 dark:border-slate-700" />

          <p className="text-sm text-slate-700 dark:text-slate-300">
            Assalamualaikum <strong>{details.parentName}</strong>, please let us know if each child below will attend
            {hasAnyResponse ? " (you can still change your response below)" : ""}:
          </p>

          {justSubmitted ? (
            <div className="mt-4 rounded-md bg-green-50 p-4 text-sm text-green-800 dark:bg-green-900/40 dark:text-green-300 animate-fade-in">
              Thank you! Your response has been recorded. You can still come back and change it any time before the
              event using this same link.
            </div>
          ) : null}

          <ul className="mt-4 space-y-3">
            {details.children.map((child) => {
              const choice: Choice = choices[child.studentId] ?? "UNDECIDED";

              return (
                <li key={child.studentId} className="rounded-md border border-slate-200 dark:border-slate-700 px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-slate-100">{child.name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Age {child.age}</p>
                    </div>
                    <span
                      className={clsx(
                        "badge",
                        choice === "ATTENDING" && "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
                        choice === "NOT_ATTENDING" && "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
                        choice === "UNDECIDED" && "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
                      )}
                    >
                      {choice === "ATTENDING" ? "Attending" : choice === "NOT_ATTENDING" ? "Not attending" : "Pending"}
                    </span>
                  </div>

                  <div className="mt-3 grid grid-cols-3 gap-1.5">
                    {(
                      [
                        ["ATTENDING", "Attending"],
                        ["NOT_ATTENDING", "Not attending"],
                        ["UNDECIDED", "Undecided"],
                      ] as [Choice, string][]
                    ).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          setChoices({ ...choices, [child.studentId]: value });
                          setJustSubmitted(false);
                        }}
                        className={clsx(
                          "rounded-md border px-2 py-1.5 text-xs font-medium transition-colors duration-150",
                          choice === value
                            ? value === "ATTENDING"
                              ? "border-green-500 bg-green-50 text-green-800 dark:border-green-500 dark:bg-green-900/40 dark:text-green-300"
                              : value === "NOT_ATTENDING"
                                ? "border-red-500 bg-red-50 text-red-800 dark:border-red-500 dark:bg-red-900/40 dark:text-red-300"
                                : "border-slate-400 bg-slate-100 text-slate-700 dark:border-slate-500 dark:bg-slate-700 dark:text-slate-200"
                            : "border-slate-200 text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-700/60",
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {choice === "NOT_ATTENDING" && (
                    <div className="mt-2 animate-fade-in">
                      <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400" htmlFor={`reason-${child.studentId}`}>
                        Reason (optional)
                      </label>
                      <textarea
                        id={`reason-${child.studentId}`}
                        className="input text-sm"
                        rows={2}
                        placeholder="e.g. clashes with another commitment, unwell, ..."
                        value={reasons[child.studentId] ?? ""}
                        onChange={(e) => {
                          setReasons({ ...reasons, [child.studentId]: e.target.value });
                          setJustSubmitted(false);
                        }}
                        maxLength={500}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          <button
            type="button"
            className="btn-primary mt-6 w-full"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? "Submitting..." : hasAnyResponse ? "Update Response" : "Submit Response"}
          </button>

          <p className="mt-4 text-center text-xs text-slate-400 dark:text-slate-500">
            Children left as "Undecided" remain pending and won't be marked as attending or not attending. You can
            return to this link at any time to update your response.
          </p>
        </div>
      </div>
    </div>
  );
}
